import { useMemo, useState } from 'react';
import { Typography } from 'antd';
import '../../styles/DescriptionBlock.css';

const { Text } = Typography;
const PREVIEW = 2;

/* ───────────────────────────────────────────────────────────
   Section headings we recognise. Everything is folded into three
   clean buckets so the output stays tidy: Overview · Key Skills ·
   Education. A line is only treated as a heading when the WHOLE
   line equals one of these — never matched inside a sentence.
─────────────────────────────────────────────────────────── */
const HEADINGS = [
  'overview',
  'summary',
  'role summary',
  'job summary',
  'role overview',
  'job description',
  'description',
  'about the role',
  'about the job',
  'job title',
  'role',
  'responsibilities',
  'roles responsibilities',
  'roles and responsibilities',
  'roles & responsibilities',
  'key responsibilities',
  'requirements',
  'required skills',
  'preferred skills',
  'technical skills',
  'key skills',
  'skills',
  'competencies',
  'nice to have',
  'good to have',
  'qualifications',
  'required qualifications',
  'preferred qualifications',
  'education',
  'experience',
  'benefits',
];
const HEADING_SET = new Set(HEADINGS);

const EDUCATION_RE = /(education|qualification)/i;
const SKILLS_RE = /(skill|competenc)/i;

const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const titleCase = (text) =>
  text.replace(/\w[^\s/]*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
const dedupe = (items) => [...new Set(items)];

/* ── HTML → clean text, keeping line breaks for block elements ── */
function htmlToText(html) {
  let text = String(html);

  text = text
    .replace(/<\s*(br|\/p|\/div|\/h[1-6]|\/li|\/tr)\s*\/?>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ');

  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"');

  return text
    .replace(/[ \t\f\v]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/* ── Break text into individual lines / bullet items ──
   Only real line breaks and bullet glyphs split a line. We deliberately do
   NOT split on " - " here: in real data a dash is usually a hyphen or range
   ("5-10 Years", "Engineering - Software & QA"), not a bullet separator. */
function splitLines(text) {
  return text
    .replace(/•/g, '\n')
    .split(/\n+/)
    .map((line) => line.trim().replace(/^[-–—•]\s*/, '').trim())
    .filter(Boolean);
}

// For a delimiter-less blob, dashes are the only bullet markers we have.
function splitBlob(text) {
  return splitLines(text.replace(/(?:^|\s)[-–—]\s+/g, '\n'));
}

// "Industry Type: IT Services & Consulting" → { fieldLabel, value }
function asMeta(line) {
  const match = line.match(/^([A-Za-z][A-Za-z&/ ]{1,28}?)\s*:\s*(\S.+)$/);
  if (!match) return null;

  const key = match[1].trim();
  const value = match[2].trim();
  // Short, label-like key + short value → it's a field, not a sentence.
  if (key.split(/\s+/).length > 3) return null;
  if (value.length > 60) return null;

  return { fieldKey: key.toLowerCase().replace(/\s+/g, '-'), fieldLabel: titleCase(key), value };
}

// Whole-line heading → which bucket it switches us into, or null.
function headingBucket(line) {
  const normalized = line.toLowerCase().replace(/[\s:.\-–—]+$/, '').trim();
  if (!HEADING_SET.has(normalized)) return null;
  if (EDUCATION_RE.test(normalized)) return 'education';
  if (SKILLS_RE.test(normalized)) return 'keySkills';
  return 'overview';
}

/* ── Primary parser: classify each line of an already-structured text ── */
function parseFromLines(lines) {
  const meta = [];
  const buckets = { overview: [], keySkills: [], education: [] };
  let current = 'overview';

  lines.forEach((line) => {
    const bucket = headingBucket(line);
    if (bucket) {
      current = bucket;
      return;
    }
    // Only the top region carries meta fields; inside Key Skills / Education a
    // "Key: Value" line (e.g. "UG: Any Graduate") is real content, not meta.
    if (current === 'overview') {
      const metaItem = asMeta(line);
      if (metaItem) {
        meta.push(metaItem);
        return;
      }
    }
    buckets[current].push(line);
  });

  return {
    meta,
    overview: dedupe(buckets.overview),
    keySkills: dedupe(buckets.keySkills),
    education: dedupe(buckets.education),
  };
}

/* ── Fallback parser: no line breaks, so split on inline heading keywords ── */
function parseFromBlob(text) {
  const ordered = [...HEADINGS].sort((a, b) => b.length - a.length);
  const headingRegex = new RegExp(`\\b(${ordered.map(escapeRegex).join('|')})\\b\\s*:?`, 'gi');
  const matches = [...text.matchAll(headingRegex)];

  if (!matches.length) {
    return { meta: [], overview: dedupe(splitBlob(text)), keySkills: [], education: [] };
  }

  const buckets = { overview: [], keySkills: [], education: [] };
  const intro = text.slice(0, matches[0].index).trim();
  if (intro) buckets.overview.push(...splitBlob(intro));

  matches.forEach((match, i) => {
    const title = match[1].trim().toLowerCase();
    const start = match.index + match[0].length;
    const end = i + 1 < matches.length ? matches[i + 1].index : text.length;
    const items = splitBlob(text.slice(start, end).trim());
    if (!items.length) return;

    const bucket = EDUCATION_RE.test(title) ? 'education' : SKILLS_RE.test(title) ? 'keySkills' : 'overview';
    buckets[bucket].push(...items);
  });

  return {
    meta: [],
    overview: dedupe(buckets.overview),
    keySkills: dedupe(buckets.keySkills),
    education: dedupe(buckets.education),
  };
}

function parseDescription(raw) {
  const text = htmlToText(raw);
  if (!text) return { meta: [], overview: [], keySkills: [], education: [] };

  const lines = splitLines(text);
  // Enough discrete lines → trust the source's own structure.
  return lines.length >= 4 ? parseFromLines(lines) : parseFromBlob(text);
}

function BulletList({ items }) {
  return (
    <ul className="jdv-ats-bullets">
      {items.map((item, i) => (
        <li key={i} className="jdv-ats-bullet-item">{item}</li>
      ))}
    </ul>
  );
}

/*
 * Frontend-only job description renderer.
 *
 * Takes a plain/simple HTML (or text) string — different for every job — and
 * turns it into the ATS template (optional meta grid + Overview / Key Skills /
 * Education) entirely in the browser. No API, no editing, nothing hardcoded.
 */
export default function DescriptionBlock({ raw = '', meta = [] }) {
  const [expanded, setExpanded] = useState(false);
  const parsed = useMemo(() => parseDescription(raw), [raw]);

  const metaRows = meta.length ? meta : parsed.meta;

  const blocks = [];
  if (parsed.overview.length) blocks.push({ key: 'overview', title: 'Overview', items: parsed.overview });
  if (parsed.keySkills.length) blocks.push({ key: 'keySkills', title: 'Key Skills', items: parsed.keySkills, card: true, icon: '🛠️' });
  if (parsed.education.length) blocks.push({ key: 'education', title: 'Education', items: parsed.education, card: true, icon: '🎓' });

  if (!metaRows.length && !blocks.length) {
    return <Text type="secondary">No description available.</Text>;
  }

  const visible = expanded ? blocks : blocks.slice(0, PREVIEW);
  const hiddenCount = blocks.length - PREVIEW;

  return (
    <div className="jdv-ats-desc">
      {metaRows.length > 0 && (
        <div className="jdv-ats-meta-grid">
          {metaRows.map(({ fieldKey, fieldLabel, value }) => (
            <div key={fieldKey ?? fieldLabel} className="jdv-ats-meta-row">
              <span className="jdv-ats-meta-key">{fieldLabel}</span>
              <span className="jdv-ats-meta-val">{value}</span>
            </div>
          ))}
        </div>
      )}

      <div className="jdv-ats-sections">
        {visible.map((block) =>
          block.card ? (
            <div key={block.key} className="education-block">
              <div className="education-header">
                <span>{block.icon}</span>
                <span>{block.title}</span>
              </div>
              <ul className="education-list">
                {block.items.map((item, i) => (
                  <li key={i} className="education-list-item">{item}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div key={block.key} className="jdv-ats-section">
              <div className="jdv-ats-section-hd"><span>{block.title}</span></div>
              <BulletList items={block.items} />
            </div>
          ),
        )}
      </div>

      {hiddenCount > 0 && (
        <span className="jdv-desc-toggle" onClick={() => setExpanded((prev) => !prev)}>
          {expanded ? 'Show less' : `Show more (${hiddenCount} more section${hiddenCount > 1 ? 's' : ''})`}
        </span>
      )}
    </div>
  );
}
